import React, { useState, useCallback, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Slider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
} from '@mui/material'
import {
  MyLocation as MyLocationIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import toast from 'react-hot-toast'
import { getCurrentPosition } from '../../utils/geofence'

// Ensure Leaflet CSS is injected once
let leafletCSSLoaded = false
function ensureLeafletCSS() {
  if (!leafletCSSLoaded) {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
    document.head.appendChild(link)
    leafletCSSLoaded = true
  }
}

/**
 * LocationPickerModal
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onConfirm: ({ latitude, longitude, geofence_radius }) => void
 *  - initialValues: { latitude, longitude, geofence_radius }
 */
const LocationPickerModal = ({ open, onClose, onConfirm, initialValues }) => {
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  const [radius, setRadius] = useState(100)
  const [detecting, setDetecting] = useState(false)
  const [mapReady, setMapReady] = useState(false)

  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const searchDebounceRef = useRef(null)

  const mapContainerRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markerRef = useRef(null)
  const circleRef = useRef(null)

  // Move map + marker + circle to a new lat/lng
  const flyTo = useCallback((lat, lng) => {
    if (!mapInstanceRef.current || !markerRef.current || !circleRef.current) return
    markerRef.current.setLatLng([lat, lng])
    circleRef.current.setLatLng([lat, lng])
    mapInstanceRef.current.setView([lat, lng], 16)
  }, [])

  // Called by Dialog TransitionProps.onEntered — fires AFTER open animation completes
  const initMap = useCallback(async () => {
    if (!mapContainerRef.current || mapInstanceRef.current) return

    ensureLeafletCSS()
    const L = (await import('leaflet')).default

    delete L.Icon.Default.prototype._getIconUrl
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    })

    const defaultLat = parseFloat(initialValues?.latitude) || 20.5937
    const defaultLng = parseFloat(initialValues?.longitude) || 78.9629
    const defaultZoom = initialValues?.latitude ? 16 : 5

    if (!mapContainerRef.current) return
    const map = L.map(mapContainerRef.current).setView([defaultLat, defaultLng], defaultZoom)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map)

    const initRadius = parseInt(initialValues?.geofence_radius) || 100
    const marker = L.marker([defaultLat, defaultLng], { draggable: true }).addTo(map)
    const circle = L.circle([defaultLat, defaultLng], { radius: initRadius, color: '#1976d2', fillOpacity: 0.15 }).addTo(map)

    marker.on('dragend', () => {
      const { lat, lng } = marker.getLatLng()
      setLatitude(lat.toFixed(7))
      setLongitude(lng.toFixed(7))
      circle.setLatLng([lat, lng])
    })

    map.on('click', (e) => {
      const { lat, lng } = e.latlng
      marker.setLatLng([lat, lng])
      circle.setLatLng([lat, lng])
      setLatitude(lat.toFixed(7))
      setLongitude(lng.toFixed(7))
    })

    mapInstanceRef.current = map
    markerRef.current = marker
    circleRef.current = circle
    setMapReady(true)

    // Pre-fill lat/lng if initial values exist
    if (initialValues?.latitude) {
      setLatitude(String(initialValues.latitude))
      setLongitude(String(initialValues.longitude))
      setRadius(parseInt(initialValues.geofence_radius) || 100)
    }
  }, [initialValues])

  // Called by TransitionProps.onExited — cleanup after close animation
  const destroyMap = useCallback(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove()
      mapInstanceRef.current = null
      markerRef.current = null
      circleRef.current = null
    }
    setMapReady(false)
    setLatitude('')
    setLongitude('')
    setRadius(100)
    setSearchQuery('')
    setSearchResults([])
  }, [])

  // Nominatim geocoding search (free, no API key)
  const searchLocation = useCallback(async (query) => {
    const q = query.trim()
    if (!q || q.length < 2) { setSearchResults([]); return }
    setSearching(true)
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=6&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      )
      const data = await res.json()
      setSearchResults(data)
    } catch {
      toast.error('Search failed. Please check your connection.')
      setSearchResults([])
    } finally {
      setSearching(false)
    }
  }, [])

  const handleSearchChange = (e) => {
    const val = e.target.value
    setSearchQuery(val)
    clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => searchLocation(val), 400)
  }

  const handleSelectResult = (result) => {
    const lat = parseFloat(result.lat)
    const lng = parseFloat(result.lon)
    setLatitude(lat.toFixed(7))
    setLongitude(lng.toFixed(7))
    flyTo(lat, lng)
    setSearchQuery(result.display_name)
    setSearchResults([])
  }

  const handleRadiusChange = (_, val) => {
    setRadius(val)
    if (circleRef.current) circleRef.current.setRadius(val)
  }

  const handleManualLatLngChange = (newLat, newLng) => {
    const lat = parseFloat(newLat)
    const lng = parseFloat(newLng)
    if (!isNaN(lat) && !isNaN(lng)) flyTo(lat, lng)
  }

  const handleDetectLocation = async () => {
    setDetecting(true)
    try {
      const { latitude: lat, longitude: lng } = await getCurrentPosition()
      const latStr = lat.toFixed(7)
      const lngStr = lng.toFixed(7)
      setLatitude(latStr)
      setLongitude(lngStr)
      flyTo(lat, lng)
      setSearchQuery('')
      setSearchResults([])
    } catch (err) {
      if (err.message === 'PERMISSION_DENIED') {
        toast.error('Location access denied. Please allow location in your browser.')
      } else {
        toast.error('Could not detect location. Please enter coordinates manually.')
      }
    } finally {
      setDetecting(false)
    }
  }

  const handleConfirm = () => {
    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)
    if (isNaN(lat) || isNaN(lng)) {
      toast.error('Please set a valid location before confirming.')
      return
    }
    onConfirm({ latitude: lat, longitude: lng, geofence_radius: radius })
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      TransitionProps={{
        onEntered: initMap,
        onExited: destroyMap,
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">Set Organization Location</Typography>
          <Typography variant="body2" color="textSecondary">
            Search an address, click the map, or drag the marker to set the office location.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pb: 2 }}>
        {/* ── Address Search Box ─────────────────────────────────── */}
        <Box sx={{ position: 'relative', mb: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search address, city, landmark…"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" color="action" />}
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearchQuery(''); setSearchResults([]) }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {/* Dropdown results */}
          {searchResults.length > 0 && (
            <Paper
              elevation={6}
              sx={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
                maxHeight: 220, overflowY: 'auto', borderRadius: 1, mt: 0.5,
              }}
            >
              <List dense disablePadding>
                {searchResults.map((r) => (
                  <ListItem key={r.place_id} disablePadding divider>
                    <ListItemButton onClick={() => handleSelectResult(r)}>
                      <ListItemText
                        primary={r.display_name}
                        primaryTypographyProps={{ variant: 'body2', noWrap: false }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>

        {/* ── Map Container ──────────────────────────────────────── */}
        <Box
          ref={mapContainerRef}
          sx={{
            height: 300, borderRadius: 2, overflow: 'hidden', mb: 2,
            border: '1px solid', borderColor: 'divider', position: 'relative',
          }}
        >
          {!mapReady && (
            <Box sx={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'action.hover', zIndex: 1,
            }}>
              <CircularProgress size={36} />
            </Box>
          )}
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Search above, click on the map, or drag the marker to pick the location.
        </Alert>

        {/* ── Manual Lat / Lng Fields + GPS Button ──────────────── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Latitude"
            value={latitude}
            onChange={(e) => { setLatitude(e.target.value); handleManualLatLngChange(e.target.value, longitude) }}
            size="small"
            type="number"
            inputProps={{ step: 'any' }}
            sx={{ flex: 1, minWidth: 150 }}
          />
          <TextField
            label="Longitude"
            value={longitude}
            onChange={(e) => { setLongitude(e.target.value); handleManualLatLngChange(latitude, e.target.value) }}
            size="small"
            type="number"
            inputProps={{ step: 'any' }}
            sx={{ flex: 1, minWidth: 150 }}
          />
          <Button
            variant="outlined"
            startIcon={detecting ? <CircularProgress size={16} /> : <MyLocationIcon />}
            onClick={handleDetectLocation}
            disabled={detecting}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {detecting ? 'Detecting…' : 'Use My Location'}
          </Button>
        </Box>

        {/* ── Radius Slider ──────────────────────────────────────── */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Geofence Radius: <strong>{radius} metres</strong>
          </Typography>
          <Slider
            value={radius}
            onChange={handleRadiusChange}
            min={50}
            max={2000}
            step={10}
            marks={[
              { value: 50, label: '50m' },
              { value: 500, label: '500m' },
              { value: 1000, label: '1km' },
              { value: 2000, label: '2km' },
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}m`}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!latitude || !longitude}
        >
          Confirm Location
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LocationPickerModal
